import { useEffect, useMemo, useState } from "react"
import { usePayment } from "./use-payment"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"
import { Card } from "@/components/ui/card"
import { useFormStore } from "@/stores/use-form-store"
import { Label } from "@/components/ui/label"
import { Check } from "lucide-react"
import type { UseFormReturn } from "react-hook-form"
import { logger } from "@/utils/logger"
import { disabledStatuses } from "./utils"
import type { CheckPaymentStatusResponse } from "@/types/form-types"
// import { PaymentStatus } from "@/types/form-types"

const Plans = ({
	form,
	paymentStatus,
}: {
	form: UseFormReturn
	paymentStatus?: CheckPaymentStatusResponse
}) => {
	const {
		activeSelfPlans,
		activeLoanPlans,
		displayTheSelfAndLoanPaymentModesTogether,
		activeTab,
		setActiveTab,
		selectedPaymentPlan,
		setSelectedPaymentPlan,
		fieldConfig,
		setValues,
		getValues,
	} = usePayment()

	const [isDisabled, setIsDisabled] = useState<boolean>(false)

	const { applicationId } = useFormStore()
	// const {
	// 	useCheckPaymentStatus,
	// 	// useHandleDownloadReceipt,
	// } = useHandlePayment(paymentParams)
	// const { data: paymentStatus } = useCheckPaymentStatus({
	// 	category: FeeCategory.TUITION,
	// 	semester: periodNumber,
	// })

	const loanDetails = useMemo(() => {
		if (paymentStatus) return paymentStatus?.loan_details
	}, [paymentStatus])
	const isPreview = !applicationId || applicationId === ""

	const paymentPlans = useMemo(() => {
		const plans = displayTheSelfAndLoanPaymentModesTogether
			? [
					...(activeSelfPlans?.map(plan => ({
						label: plan.name,
						value: `self-${plan.component_id}`,
						discount: plan.discount,
						finalPrice: plan.finalPrice,
						actualPrice: plan.price,
					})) || []),
					...(activeLoanPlans?.map(plan => ({
						label: plan.name,
						value: `loan-${plan.component_id}`,
						discount: plan.discount,
						finalPrice: plan.finalPrice,
						actualPrice: plan.price,
					})) || []),
				]
			: activeTab === "self-payment"
				? activeSelfPlans?.map(plan => ({
						label: plan.name,
						value: `self-${plan.component_id}`,
						discount: plan.discount,
						finalPrice: plan.finalPrice,
						actualPrice: plan.price,
					})) || []
				: activeLoanPlans?.map(plan => ({
						label: plan.name,
						value: `loan-${plan.component_id}`,
						discount: plan.discount,
						finalPrice: plan.finalPrice,
						actualPrice: plan.price,
					})) || []
		return plans
	}, [
		displayTheSelfAndLoanPaymentModesTogether,
		activeTab,
		activeSelfPlans,
		activeLoanPlans,
	])

	useEffect(() => {
		if (loanDetails?.applicant?.additional_details?.plan_id) {
			setIsDisabled(true)
			setSelectedPaymentPlan(
				loanDetails?.applicant?.additional_details?.plan_id || "",
			)
			setActiveTab("loan-payment")
			form.setValue(fieldConfig?.field_id + ".payment_type", "loan", {
				shouldValidate: false,
				shouldDirty: false,
				shouldTouch: false,
			})
		}
	}, [loanDetails])

	const OptionType = (id: string) => {
		return id.startsWith("self") ? "Self" : "Loan"
	}

	const containsInDisabled = () => {
		return disabledStatuses.includes(
			form.watch(fieldConfig?.field_id + ".payment_status"),
		)
	}
	logger.info(form.watch(fieldConfig?.field_id + ".payment_status"))
	return (
		<div>
			<style>{`
                .custom-tab[data-state="active"] {
                    background: linear-gradient(178.25deg, #155DFC 1.48%, #0D3796 156.38%) !important;
                    color: white !important;
                }
                .custom-tab[data-state="inactive"] {
                    background: transparent !important;
                    color: #6B7280 !important;
                }
            `}</style>
			{!displayTheSelfAndLoanPaymentModesTogether &&
				activeSelfPlans.length > 0 &&
				activeLoanPlans.length > 0 && (
					<div className="mb-8">
						<h2 className="mb-6 text-sm font-medium">
							Select Payment Mode
						</h2>
						<Tabs
							// defaultValue={activeTab}
							value={activeTab}
							className="w-full"
							onValueChange={setActiveTab}
						>
							<TabsList className="h-fit w-full rounded-full bg-muted p-1.5">
								{activeSelfPlans.length > 0 && (
									<TabsTrigger
										disabled={
											isDisabled && containsInDisabled()
										}
										value="self-payment"
										className="custom-tab w-1/2 rounded-full py-3 text-sm font-normal"
									>
										Self-Payment
									</TabsTrigger>
								)}
								{activeLoanPlans.length > 0 && (
									<TabsTrigger
										disabled={
											isDisabled && containsInDisabled()
										}
										value="loan-payment"
										className="custom-tab w-1/2 rounded-full py-3 text-sm font-normal"
									>
										Loan-Payment
									</TabsTrigger>
								)}
							</TabsList>
						</Tabs>
					</div>
				)}
			<div className="mb-9">
				<h2 className="mb-4 text-sm font-medium">
					Choose Your Payment Plan
				</h2>
				<RadioGroup
					value={selectedPaymentPlan}
					disabled={isDisabled && containsInDisabled()}
					onValueChange={value => {
						setSelectedPaymentPlan(value)
						// if the value id starts with self
						if (value.startsWith("self")) {
							setValues({
								payment_type: "self",
							})
						}
						// if the value id starts with loan
						if (value.startsWith("loan")) {
							setValues({
								payment_type: "loan",
							})
						}
						logger.info(getValues())
					}}
					className={cn(
						"df-radio-group",
						"grid grid-cols-1 gap-4 sm:grid-cols-2",
					)}
				>
					{paymentPlans.length > 0 &&
						paymentPlans?.map((option, index) => (
							<Card
								key={option.value}
								className={cn(
									"relative cursor-pointer transition-all duration-200 hover:shadow-md",
									"border-2 px-5 py-6 hover:border-primary/50",
									selectedPaymentPlan === option.value &&
										"border-primary bg-primary/5 shadow-md",
								)}
							>
								<div
									className={cn(
										"flex items-start space-x-3",
										"items-center",
									)}
								>
									<RadioGroupItem
										required={fieldConfig.is_required}
										value={option.value}
										id={option.value}
										className={cn("mt-0.5")}
									/>
									<div className="min-w-0 flex-1">
										<Label
											htmlFor={`${fieldConfig.field_id}_${index}`}
											className={cn(
												"block cursor-pointer text-xs font-normal leading-relaxed",
												selectedPaymentPlan ===
													option.value &&
													"font-medium",
											)}
										>
											{option.label}{" "}
											<span className="text-xs">
												({OptionType(option.value)})
											</span>
										</Label>

										{!isPreview && (
											<p>
												Pay{" "}
												<span>
													{" "}
													{option.finalPrice}{" "}
												</span>
												{option.actualPrice !==
													option.finalPrice && (
													<span className="text-[#EC6D62] line-through">
														{option.actualPrice}
													</span>
												)}
											</p>
										)}
									</div>
									{selectedPaymentPlan === option.value && (
										<Check className="h-4 w-4 flex-shrink-0 text-primary" />
									)}
								</div>
							</Card>
						))}
				</RadioGroup>
			</div>
		</div>
	)
}

export default Plans
